import React, { useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import {
  LayoutDashboard, Users, Store, Package, Ticket, ScanLine, Wallet, IndianRupee,
  Gift, Sparkles, Megaphone, Upload, BarChart3, Database, Settings, ShieldAlert,
  ScrollText, MessageCircle, LogOut, Menu, UserCog, X, Bot, LifeBuoy, SearchCode,
} from "lucide-react";
import AgentSessions from "@/admin/AgentSessions";
import MemberRequests from "@/admin/MemberRequests";
import CouponLookup from "@/admin/CouponLookup";
import Dashboard from "@/admin/Dashboard";
import Imports from "@/admin/Imports";
import Campaigns from "@/admin/Campaigns";
import GiftsAdmin from "@/admin/GiftsAdmin";
import UPIQueue from "@/admin/UPIQueue";
import ConfigPage from "@/admin/ConfigPage";
import Simulator from "@/admin/Simulator";
import {
  Dealers, Products, Painters, Batches, Scans, Ledger, AuditLogs, AdminUsers, Fraud, ERP, Reports,
} from "@/admin/ListPages";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", testid: "dashboard", end: true },
  { to: "/admin/painters", icon: Users, label: "Users & Painters", testid: "users" },
  { to: "/admin/dealers", icon: Store, label: "Dealers", testid: "dealers" },
  { to: "/admin/products", icon: Package, label: "Products", testid: "products" },
  { to: "/admin/batches", icon: Ticket, label: "Coupon Batches", testid: "batches" },
  { to: "/admin/coupon-lookup", icon: SearchCode, label: "Coupon Lookup", testid: "coupon-lookup" },
  { to: "/admin/scans", icon: ScanLine, label: "Scans", testid: "scans" },
  { to: "/admin/ledger", icon: Wallet, label: "Wallet Ledger", testid: "ledger" },
  { to: "/admin/upi", icon: IndianRupee, label: "UPI Redemptions", testid: "upi" },
  { to: "/admin/gifts", icon: Gift, label: "Gifts & Orders", testid: "gifts" },
  { to: "/admin/recommendations", icon: Sparkles, label: "AI Recommendations", testid: "recommendations" },
  { to: "/admin/campaigns", icon: Megaphone, label: "Campaigns", testid: "campaigns" },
  { to: "/admin/imports", icon: Upload, label: "Data Imports", testid: "import" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports", testid: "reports" },
  { to: "/admin/erp", icon: Database, label: "ERP Dashboards", testid: "erp" },
  { to: "/admin/fraud", icon: ShieldAlert, label: "Fraud Flags", testid: "fraud" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Logs", testid: "audit" },
  { to: "/admin/users-admin", icon: UserCog, label: "User Management", testid: "user-mgmt" },
  { to: "/admin/config", icon: Settings, label: "Config", testid: "config" },
  { to: "/admin/simulator", icon: MessageCircle, label: "WhatsApp Simulator", testid: "simulator" },
  { to: "/admin/agent-sessions", icon: Bot, label: "Agent Sessions", testid: "agent-sessions" },
  { to: "/admin/member-requests", icon: LifeBuoy, label: "Member Requests", testid: "member-requests" },
];

const ROLE_LABEL = {
  super_admin: "Super Admin", jewell_admin: "Jewell Admin", campaign_approver: "Campaign Approver",
  operations: "Operations", viewer: "Viewer",
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed md:static z-40 inset-y-0 left-0 w-64 bg-[#111827] text-white flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
          <Brand dark compact />
          <button className="md:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`admin-sidebar-nav-${n.testid}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-[#8B0000] text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"}`}>
              <n.icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="h-5 w-5" /></button>
            <button onClick={() => nav("/")} className="text-sm text-gray-500 hover:text-[#8B0000] hidden sm:block">← Home</button>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-gray-900">{user?.name}</div>
              <div className="text-xs text-[#8B0000] font-medium">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#8B0000] text-white flex items-center justify-center font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() || "A"}
            </div>
            <button onClick={async () => { await logout(); nav("/login"); }} data-testid="admin-logout-btn"
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Logout"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-x-hidden">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="painters" element={<Painters />} />
            <Route path="dealers" element={<Dealers />} />
            <Route path="products" element={<Products />} />
            <Route path="batches" element={<Batches />} />
            <Route path="scans" element={<Scans />} />
            <Route path="ledger" element={<Ledger />} />
            <Route path="upi" element={<UPIQueue />} />
            <Route path="gifts" element={<GiftsAdmin />} />
            <Route path="recommendations" element={<Campaigns tab="recommendations" />} />
            <Route path="campaigns" element={<Campaigns tab="campaigns" />} />
            <Route path="imports" element={<Imports />} />
            <Route path="reports" element={<Reports />} />
            <Route path="erp" element={<ERP />} />
            <Route path="fraud" element={<Fraud />} />
            <Route path="audit" element={<AuditLogs />} />
            <Route path="users-admin" element={<AdminUsers />} />
            <Route path="config" element={<ConfigPage />} />
            <Route path="simulator" element={<Simulator />} />
            <Route path="agent-sessions" element={<AgentSessions />} />
            <Route path="member-requests" element={<MemberRequests />} />
            <Route path="coupon-lookup" element={<CouponLookup />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
